/**
 * Every placeholder the Scenarios page draws on, isolated here and banner-commented so a real
 * feed can replace one without a repo-wide search. Nothing outside this file should invent a
 * number the real snapshot (`@/lib/data`) could otherwise carry — see the module doc on
 * `lib/data/scenario.ts` for what IS real.
 *
 * `SYSTEMS`' baseline `shareOfCeiling` and `addFacesFraction` are FRACTIONS of the prototype's
 * canon ceiling (33,371 — the real ceiling reconciles with it almost to the digit, per the plan's
 * de-risking finding), not absolute face counts, so an edited or drifted real ceiling still
 * carries three systems that sum to it.
 */

import type {
  Band,
  LeverCause,
  LeverFamily,
  ScenarioContext,
  ScenarioParams,
  Sku,
} from "@/lib/data";

/* ───────────────────────── MOCK: SYSTEMS ─────────────────────────
 * The three named storage systems and their unit economics. No feed in this data names which
 * physical machine a pick face belongs to or what a unit of each costs — build handover §2/§4. */

export type SystemKey = "aframe" | "p2l" | "perfect";

export interface MockSystem {
  key: SystemKey;
  name: string;
  affinity: string;
  /** Baseline face share of the canon ceiling (4,800 / 18,600 / 9,971 over 33,371). */
  shareOfCeiling: number;
  /** Faces added per unit, as a fraction of the (real) ceiling. */
  addFacesFraction: number;
  addCostM: number;
  addLeadMonths: number;
  addM2: number;
}

const CANON_CEIL = 33371;

export const SYSTEMS: MockSystem[] = [
  {
    key: "aframe",
    name: "A-Frame",
    affinity: "A · fast movers",
    shareOfCeiling: 4800 / CANON_CEIL,
    addFacesFraction: 2400 / CANON_CEIL,
    addCostM: 0.9,
    addLeadMonths: 6,
    addM2: 300,
  },
  {
    key: "p2l",
    name: "Pick-to-Light",
    affinity: "B · medium movers",
    shareOfCeiling: 18600 / CANON_CEIL,
    addFacesFraction: 6200 / CANON_CEIL,
    addCostM: 2.1,
    addLeadMonths: 12,
    addM2: 620,
  },
  {
    key: "perfect",
    name: "Perfect Pick",
    affinity: "C and D · slow movers",
    shareOfCeiling: 9971 / CANON_CEIL,
    addFacesFraction: 2000 / CANON_CEIL,
    addCostM: 1.4,
    addLeadMonths: 9,
    addM2: 450,
  },
];

export const SYSTEM_BY_KEY: Record<SystemKey, MockSystem> = {
  aframe: SYSTEMS[0] as MockSystem,
  p2l: SYSTEMS[1] as MockSystem,
  perfect: SYSTEMS[2] as MockSystem,
};

/** `ScenarioContext.systemAddFacesFraction` — see `lib/data/scenario.ts`. */
export const SYSTEM_ADD_FACES_FRACTION: Record<SystemKey, number> = {
  aframe: SYSTEM_BY_KEY.aframe.addFacesFraction,
  p2l: SYSTEM_BY_KEY.p2l.addFacesFraction,
  perfect: SYSTEM_BY_KEY.perfect.addFacesFraction,
};

/* ───────────────────────── MOCK: PLACEMENT (home system + face caps) ─────────────────────────
 * Whether slotting data exists at SKU-by-system level is unconfirmed (build handover §10) — so
 * placement is DERIVED, not tabulated: every SKU belongs in its band's home system, and the site
 * fills each system up to its real face cap. A SKU whose home system is already full overflows
 * into the first system that still has a free face, and counts as MIS-SLOTTED there — it is only
 * "correctly slotted" when it sits in its own home system.
 *
 * That derivation is what makes the four capacity rules hold by construction (see the module doc
 * on `pick-faces.tsx` / the `capacity-scenario.check.mts` harness):
 *   1. every SKU is placed exactly once  → Σ in-use == the population placed
 *   2. `systemFaces` sums EXACTLY to the ceiling → Σ faces == ceiling (+ added units)
 *   3. free is `faces − in-use`, never clamped → Σ free == ceiling − in-use (can go negative)
 *   4. correctly-slotted are only ever home-band SKUs, capped at the face count → never exceeds
 *      the home band's own size.
 *
 * Home systems: A → A-Frame, B → Pick-to-Light, C and D → Perfect Pick. */

/** Each band's home storage system. C and D share Perfect Pick, so its "home band" is C+D. */
export const HOME_SYSTEM: Record<Band, SystemKey> = {
  A: "aframe",
  B: "p2l",
  C: "perfect",
  D: "perfect",
};

/** The order overflow fills free faces in — fixed, so placement is reproducible across renders. */
const SYSTEM_ORDER: readonly SystemKey[] = ["aframe", "p2l", "perfect"];

/**
 * Baseline faces per system, summing EXACTLY to `ceil` — the last system absorbs the rounding
 * remainder so Σ faces reconciles to the ceiling to the digit (capacity rule 2). Rounding each
 * share independently could leave the three a face or two off the ceiling; this cannot.
 */
export function systemFaces(ceil: number): Record<SystemKey, number> {
  const aframe = Math.round(ceil * SYSTEM_BY_KEY.aframe.shareOfCeiling);
  const p2l = Math.round(ceil * SYSTEM_BY_KEY.p2l.shareOfCeiling);
  const perfect = Math.round(ceil) - aframe - p2l;
  return { aframe, p2l, perfect };
}

export interface Placement {
  system: SystemKey;
  misslot: boolean;
}

/**
 * A stable, deterministic per-SKU placement — the SAME assignment feeds the Pick Faces dot grid's
 * occ/mis counts AND the scatter's per-point system/mis-slot metadata (hover, filter), so the two
 * can never disagree. Ids are sorted so the fill order is reproducible without re-shuffling.
 *
 * Pass 1 slots every SKU into its home system up to that system's face cap (correctly slotted);
 * pass 2 spills each home-full SKU into the first system with a free face (mis-slotted). If the
 * whole site is over capacity (population > ceiling) the leftover parks back in its own home
 * system — still correctly slotted BY BAND, so mis-slot stays strictly "outside home system"; the
 * over-subscription surfaces instead as that system's free count going negative (rule 3), never as
 * an inflated mis-slot count.
 */
export function assignPlacements(skus: readonly Sku[], ceil: number): Map<number, Placement> {
  const remaining = systemFaces(ceil);
  const out = new Map<number, Placement>();

  const byHome: Record<SystemKey, number[]> = { aframe: [], p2l: [], perfect: [] };
  const homeOf = new Map<number, SystemKey>();
  for (const s of skus) {
    const home = HOME_SYSTEM[s.band];
    byHome[home].push(s.id);
    homeOf.set(s.id, home);
  }

  // Pass 1 — correctly slotted: home-band SKUs, up to each system's face cap.
  const overflow: number[] = [];
  for (const sys of SYSTEM_ORDER) {
    for (const id of byHome[sys].slice().sort((a, b) => a - b)) {
      if (remaining[sys] > 0) {
        out.set(id, { system: sys, misslot: false });
        remaining[sys]--;
      } else {
        overflow.push(id);
      }
    }
  }

  // Pass 2 — overflow: home system full, so mis-slot into the first system with a free face.
  for (const id of overflow.sort((a, b) => a - b)) {
    const sys = SYSTEM_ORDER.find((k) => remaining[k] > 0);
    if (sys !== undefined) {
      out.set(id, { system: sys, misslot: true });
      remaining[sys]--;
    } else {
      // Site over capacity: nowhere free. Park back in the home system — still correctly slotted
      // by band (mis-slot means "outside home system"); the over-subscription shows as this
      // system's free going negative, not as a mis-slot.
      out.set(id, { system: homeOf.get(id) as SystemKey, misslot: false });
    }
  }
  return out;
}

export interface SystemFigures {
  key: SystemKey;
  name: string;
  affinity: string;
  faces: number;
  /** Correctly slotted (home-band SKUs in this system). */
  occ: number;
  /** Mis-slotted (overflow parked here from a full home system). */
  mis: number;
  /** `faces − occ − mis`. NEVER clamped — a negative value is real overflow, not zero. */
  free: number;
  added: number;
}

/**
 * The three systems' occ / mis / free / faces under the current lever mix — the SINGLE source
 * both the Pick Faces panel (`pick-faces.tsx`) and the `capacity-scenario.check.mts` harness
 * read, so a rendered figure and an asserted one can never drift. Released SKUs (claimed by a
 * lever) have vacated their face and are skipped. `free` is left un-clamped on purpose (rule 3).
 */
export function systemFigures(
  skus: readonly Sku[],
  placements: ReadonlyMap<number, Placement>,
  claim: ReadonlyMap<number, LeverCause>,
  draft: ScenarioParams,
  ctx: ScenarioContext,
): SystemFigures[] {
  const occ: Record<SystemKey, number> = { aframe: 0, p2l: 0, perfect: 0 };
  const mis: Record<SystemKey, number> = { aframe: 0, p2l: 0, perfect: 0 };
  for (const s of skus) {
    if (claim.has(s.id)) {
      continue; // released — no longer occupying a face
    }
    const p = placements.get(s.id);
    if (!p) {
      continue;
    }
    if (p.misslot) {
      mis[p.system]++;
    } else {
      occ[p.system]++;
    }
  }
  const baseFaces = systemFaces(ctx.model.capd.ceil);
  return SYSTEMS.map((sys) => {
    const added =
      draft.capSystem === sys.key
        ? Math.round(draft.capUnits * sys.addFacesFraction * ctx.model.capd.ceil)
        : 0;
    const faces = baseFaces[sys.key] + added;
    return {
      key: sys.key,
      name: sys.name,
      affinity: sys.affinity,
      faces,
      occ: occ[sys.key],
      mis: mis[sys.key],
      free: faces - occ[sys.key] - mis[sys.key],
      added,
    };
  });
}

/* ───────────────────────── MOCK: FAMILY_WAIVER / FAMILY_MDR ─────────────────────────
 * Per-family/set VALUES are real (`LeverFamily.base`/`perStop`); which family sits on a
 * contracted range, and which fitting set is regulatory-cleared, are not carried by any feed —
 * build handover §10. Assigned by real rank (families sorted by `base`, descending — the same
 * order the pack/convert dials keep), so they key off whichever real family ids the drop
 * actually serves, gracefully producing nothing beyond the population it's handed. */

/** The 5th and 6th pack family by `base` sit on a named account's contracted range. */
export function familyWaiverAccounts(families: readonly LeverFamily[]): Record<string, string> {
  const sorted = families.slice().sort((a, b) => b.base - a.base);
  const out: Record<string, string> = {};
  const names = ["Leightons", "Scrivens"];
  sorted.slice(4, 6).forEach((f, i) => {
    out[f.id] = names[i] ?? "named account";
  });
  return out;
}

export type MdrStatus = "certified" | "pending" | "legacy";

/** The first four fitting sets by `base` are EU MDR certified; beyond that, pending then legacy
 *  — mirrors the prototype's `CONV_MDR` table, applied to whichever sets the drop serves. */
export function familyMdrStatus(families: readonly LeverFamily[]): Record<string, MdrStatus> {
  const sorted = families.slice().sort((a, b) => b.base - a.base);
  const out: Record<string, MdrStatus> = {};
  sorted.forEach((f, i) => {
    out[f.id] = i < 4 ? "certified" : i < 6 ? "pending" : "legacy";
  });
  return out;
}

/** Fitting sets past this index are not yet regulatory-cleared (build handover §4). */
export const CONV_CERTIFIED_MAX = 4;
/** Pack families at or past this count trigger the two named waivers above. */
export const PACK_WAIVER_THRESHOLD = 6;

/* ───────────────────────── MOCK: ACCOUNTS ─────────────────────────
 * The named account list in the Customers detail — no feed in this data carries account names,
 * tiers or monthly values (build handover §10). `slow: true` marks the accounts sitting on the
 * slow-moving ranges the levers touch; `waiver: true` the two on contracted range. */

export interface MockAccount {
  name: string;
  tier: "Diamond" | "Platinum" | "Gold";
  country: string;
  monthlyM: number;
  slow: boolean;
  waiver?: boolean;
}

export const ACCOUNTS: MockAccount[] = [
  {
    name: "Specsavers Group",
    tier: "Diamond",
    country: "United Kingdom",
    monthlyM: 2.34,
    slow: false,
  },
  {
    name: "Vision Express",
    tier: "Diamond",
    country: "United Kingdom",
    monthlyM: 2.05,
    slow: true,
  },
  { name: "Fielmann", tier: "Diamond", country: "Germany", monthlyM: 1.71, slow: false },
  {
    name: "Boots Opticians",
    tier: "Platinum",
    country: "United Kingdom",
    monthlyM: 1.48,
    slow: true,
  },
  {
    name: "GrandVision Benelux",
    tier: "Platinum",
    country: "Netherlands",
    monthlyM: 1.12,
    slow: true,
  },
  { name: "Optic 2000", tier: "Platinum", country: "France", monthlyM: 0.96, slow: false },
  {
    name: "Vision Ireland Group",
    tier: "Platinum",
    country: "Ireland",
    monthlyM: 0.61,
    slow: true,
  },
  { name: "Optyk Centrum", tier: "Platinum", country: "Poland", monthlyM: 0.44, slow: true },
  {
    name: "Leightons",
    tier: "Gold",
    country: "United Kingdom",
    monthlyM: 0.38,
    slow: true,
    waiver: true,
  },
  {
    name: "Scrivens",
    tier: "Gold",
    country: "United Kingdom",
    monthlyM: 0.33,
    slow: true,
    waiver: true,
  },
  { name: "Optegra Clinics", tier: "Gold", country: "United Kingdom", monthlyM: 0.29, slow: false },
  { name: "Hakim Optical EU", tier: "Gold", country: "France", monthlyM: 0.22, slow: true },
];

/* ───────────────────────── MOCK: SECOND_SITE / PLANTS ─────────────────────────
 * The Channels view's whole plant→site→route story — no feed in this data carries a second
 * site's load, an inbound plant split, or route-level served-from figures (build handover §10).
 * The SCALING factor (how far load moves) is real — `lib/data/scenario.ts`'s `channelShare`,
 * off relocated SKUs' actual NTS value — only the baseline/target and the route table are mock. */

export const SECOND_SITE = {
  name: "PLDC",
  loadBefore: 82.7,
  loadAfter: 97.3,
};

export const PLANTS = [
  { name: "Limerick", share: 88 },
  { name: "Jacksonville", share: 12 },
];

export interface MockRoute {
  name: string;
  share: number;
  /** Percentage points of TOTAL volume this route moves at full relocation engagement. */
  movesAtFull: number;
  opensAtFull?: boolean;
}

export const ROUTES: MockRoute[] = [
  { name: "Retail chain", share: 54, movesAtFull: 3.1 },
  { name: "Independent", share: 19, movesAtFull: 1.3 },
  { name: "Hospital / clinic", share: 14, movesAtFull: 0 },
  { name: "Distributor", share: 9, movesAtFull: 2.8 },
  { name: "Direct to patient", share: 4, movesAtFull: 0, opensAtFull: true },
];
/** The relocate engagement (band-D released ÷ real D count) the route table above was fit at. */
export const ROUTES_FULL_RELOC_MOVE_PCT = 7.2;

/* ───────────────────────── MOCK: FLOOR_ENVELOPE_M2 ───────────────────────── */

export const FLOOR_ENVELOPE_M2 = 1800;
